/**
 * Label helpers for wedding roles, access levels, and statuses.
 * Provides Czech translations for display purposes.
 */

/**
 * Maps wedding member role to Czech label.
 * Currently uses wedding_members.role (admin, coordinator, viewer).
 * 
 * Future: When personal_role field is added to wedding_members table,
 * this function should be updated to use personal_role instead.
 * 
 * @param role - The role string (admin, coordinator, viewer, or future personal roles)
 * @returns Czech label for the role
 */
export function getRoleLabel(role: string): string {
  const roleMap: Record<string, string> = {
    // wedding_members.role: admin = správce svatby, coordinator = koordinátor, viewer = host
    admin: 'Admin svatby',
    coordinator: 'Koordinátor',
    viewer: 'Host',
    
    // Future personal roles (for when personal_role field is added)
    groom: 'Ženich',
    bride: 'Nevěsta',
    witness: 'Svědek / svědkyně',
    guest: 'Host',
    vendor: 'Dodavatel',
    photographer: 'Fotograf',
    other: 'Jiná',
  };
  
  return roleMap[role] || 'Neznámá';
}

/**
 * Maps access level to Czech label.
 * Access levels indicate permission level (owner, admin, editor, viewer).
 * 
 * @param access - The access level string
 * @returns Czech label for the access level
 */
export function getAccessLabel(access: string): string {
  const accessMap: Record<string, string> = {
    owner: 'Vlastník',
    admin: 'Správce',
    coordinator: 'Editor', // coordinator role maps to editor access
    editor: 'Editor',
    viewer: 'Čtení',
  };
  
  return accessMap[access] || 'Neznámá';
}

/**
 * Maps wedding status to Czech label.
 * Status can be: active, draft, completed, archived
 * 
 * @param status - The status string
 * @returns Czech label for the status
 */
export function getStatusLabel(status: string): string {
  const statusMap: Record<string, string> = {
    active: 'Aktivní',
    draft: 'Koncept',
    completed: 'Uplýnulá',
    archived: 'Uplýnulá',
  };
  
  return statusMap[status] || 'Neznámá';
}

/**
 * Helper to determine access level from wedding and user context.
 * 
 * @param wedding - The wedding object
 * @param userId - The current user's ID
 * @param memberRole - The user's role from wedding_members table
 * @returns The access level (owner, admin, coordinator, viewer)
 */
export function getAccessLevel(
  wedding: { owner_user_id: string | null },
  userId: string,
  memberRole?: string
): 'owner' | 'admin' | 'coordinator' | 'viewer' {
  // Owner has full access - check if user is the owner
  if (wedding.owner_user_id && wedding.owner_user_id === userId) {
    return 'owner';
  }
  
  // Otherwise use member role
  if (memberRole === 'admin') {
    return 'admin';
  }
  if (memberRole === 'coordinator') {
    return 'coordinator';
  }
  
  // Default to viewer
  return 'viewer';
}
